import { useState } from "react";
import {
  Table,
  Text,
  Group,
  Button,
  Badge,
  Alert,
  Modal,
  TextInput,
  Stack,
  Tabs,
  Card,
  SimpleGrid,
  Checkbox,
  Grid,
  Box,
  Paper,
  ScrollArea,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconUsers,
  IconShieldLock,
  IconUser,
  IconSettings,
} from "@tabler/icons-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { PageHeader } from "../../components/PageHeader";
import { ConfirmationModal } from "../../components/ConfirmationModal";
import { showSuccess } from "../../lib/notifications";
import { useAuth } from "../../contexts/AuthContext";
import { usersApi } from "../../lib/api";
import { LoadingOverlay } from "../../components/LoadingOverlay";
import { PageTransition } from "../../components/PageTransition";
import { UserRole, User } from "../../types";

interface RolePermissions {
  services: {
    create: boolean;
    edit: boolean;
    delete: boolean;
    view: boolean;
  };
  environments: {
    create: boolean;
    edit: boolean;
    delete: boolean;
    view: boolean;
  };
  variables: {
    create: boolean;
    edit: boolean;
    delete: boolean;
    view: boolean;
    viewSecrets: boolean;
  };
  users: {
    manage: boolean;
    view: boolean;
  };
}

const roleDescriptions = {
  [UserRole.SUPERUSER]: "Full access to all features and settings",
  [UserRole.ADMIN]: "Can manage users, services, and environments",
  [UserRole.DEVOPS]: "Can manage services and environments",
  [UserRole.DEVELOPER]: "Can view and use services",
  [UserRole.AUDITOR]: "Read-only access to audit system activity",
};

const rolePermissions: Record<UserRole, RolePermissions> = {
  [UserRole.SUPERUSER]: {
    services: { create: true, edit: true, delete: true, view: true },
    environments: { create: true, edit: true, delete: true, view: true },
    variables: {
      create: true,
      edit: true,
      delete: true,
      view: true,
      viewSecrets: true,
    },
    users: { manage: true, view: true },
  },
  [UserRole.ADMIN]: {
    services: { create: true, edit: true, delete: true, view: true },
    environments: { create: true, edit: true, delete: true, view: true },
    variables: {
      create: true,
      edit: true,
      delete: true,
      view: true,
      viewSecrets: true,
    },
    users: { manage: true, view: true },
  },
  [UserRole.DEVOPS]: {
    services: { create: true, edit: true, delete: false, view: true },
    environments: { create: true, edit: true, delete: true, view: true },
    variables: {
      create: true,
      edit: true,
      delete: true,
      view: true,
      viewSecrets: true,
    },
    users: { manage: false, view: true },
  },
  [UserRole.DEVELOPER]: {
    services: { create: false, edit: false, delete: false, view: true },
    environments: { create: false, edit: false, delete: false, view: true },
    variables: {
      create: true,
      edit: true,
      delete: false,
      view: true,
      viewSecrets: false,
    },
    users: { manage: false, view: true },
  },
  [UserRole.AUDITOR]: {
    services: { create: false, edit: false, delete: false, view: true },
    environments: { create: false, edit: false, delete: false, view: true },
    variables: {
      create: false,
      edit: false,
      delete: false,
      view: true,
      viewSecrets: false,
    },
    users: { manage: false, view: true },
  },
};

export function AdminUsersPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [userDetailsModalOpen, setUserDetailsModalOpen] = useState(false);
  const [suspendModalOpen, setSuspendModalOpen] = useState(false);
  const [activateModalOpen, setActivateModalOpen] = useState(false);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState<string | null>("users");
  const [search, setSearch] = useState("");

  const isAdmin = ["SUPERUSER", "ADMIN"].includes(user?.role || "");

  const { data: users, isLoading: isLoadingUsers } = useQuery({
    queryKey: ["users"],
    queryFn: async () => {
      try {
        const response = await usersApi.list();
        return response.data;
      } catch (error) {
        console.error("Failed to fetch users:", error);
        throw error;
      }
    },
  });

  const suspendUser = useMutation({
    mutationFn: async (userId: string) => {
      const response = await usersApi.suspend(userId);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["users"]);
      showSuccess("User suspended successfully");
    },
    onError: (error: any) => {
      setError(error.response?.data?.error || "Failed to suspend user");
    },
  });

  const activateUser = useMutation({
    mutationFn: async (userId: string) => {
      const response = await usersApi.activate(userId);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["users"]);
      showSuccess("User activated successfully");
    },
    onError: (error: any) => {
      setError(error.response?.data?.error || "Failed to activate user");
    },
  });

  if (isLoadingUsers) {
    return <LoadingOverlay message="Loading..." />;
  }

  // Filter users based on search
  const filteredUsers = users?.filter((user: User) =>
    search
      ? user.firstName.toLowerCase().includes(search.toLowerCase()) ||
        user.lastName.toLowerCase().includes(search.toLowerCase()) ||
        user.email.toLowerCase().includes(search.toLowerCase()) ||
        user.role.toLowerCase().includes(search.toLowerCase())
      : true
  );

  const renderPermissionCard = (role: UserRole) => {
    const permissions = rolePermissions[role];
    return (
      <Card shadow="sm" p="lg" radius="md" withBorder>
        <Group position="apart" mb="xs">
          <Text weight={500}>{role}</Text>
          <Badge color={role === "SUPERUSER" ? "red" : "blue"}>
            {users?.filter((u: any) => u.role === role).length || 0} users
          </Badge>
        </Group>

        <Text size="sm" color="dimmed" mb="md">
          {roleDescriptions[role]}
        </Text>

        <Stack spacing="xs">
          <Text size="sm" weight={500}>
            Services
          </Text>
          <Group spacing="xs">
            <Checkbox
              readOnly
              checked={permissions.services.view}
              label="View"
            />
            <Checkbox
              readOnly
              checked={permissions.services.create}
              label="Create"
            />
            <Checkbox
              readOnly
              checked={permissions.services.edit}
              label="Edit"
            />
            <Checkbox
              readOnly
              checked={permissions.services.delete}
              label="Delete"
            />
          </Group>

          <Text size="sm" weight={500} mt="sm">
            Environments
          </Text>
          <Group spacing="xs">
            <Checkbox
              readOnly
              checked={permissions.environments.view}
              label="View"
            />
            <Checkbox
              readOnly
              checked={permissions.environments.create}
              label="Create"
            />
            <Checkbox
              readOnly
              checked={permissions.environments.edit}
              label="Edit"
            />
            <Checkbox
              readOnly
              checked={permissions.environments.delete}
              label="Delete"
            />
          </Group>

          <Text size="sm" weight={500} mt="sm">
            Variables
          </Text>
          <Group spacing="xs">
            <Checkbox
              readOnly
              checked={permissions.variables.view}
              label="View"
            />
            <Checkbox
              readOnly
              checked={permissions.variables.create}
              label="Create"
            />
            <Checkbox
              readOnly
              checked={permissions.variables.edit}
              label="Edit"
            />
            <Checkbox
              readOnly
              checked={permissions.variables.delete}
              label="Delete"
            />
            <Checkbox
              readOnly
              checked={permissions.variables.viewSecrets}
              label="View Secrets"
            />
          </Group>

          <Text size="sm" weight={500} mt="sm">
            Users
          </Text>
          <Group spacing="xs">
            <Checkbox readOnly checked={permissions.users.view} label="View" />
            <Checkbox
              readOnly
              checked={permissions.users.manage}
              label="Manage"
            />
          </Group>
        </Stack>
      </Card>
    );
  };

  return (
    <PageTransition>
      <PageHeader
        title="User Management"
        description="Manage users, roles, and deboarding processes"
      />

      {error && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          color="red"
          mb="md"
          title="Error"
          onClose={() => setError("")}
          withCloseButton
        >
          {error}
        </Alert>
      )}

      <Tabs value={activeTab} onTabChange={setActiveTab} mb="md">
        <Tabs.List sx={{ flexWrap: 'nowrap', overflowX: 'auto' }}>
          <Tabs.Tab value="users" icon={<IconUsers size={14} />}>
            Users
          </Tabs.Tab>
          <Tabs.Tab value="roles" icon={<IconShieldLock size={14} />}>
            Roles
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="users">
          <Card mt="md" shadow="sm" radius="md" withBorder>
            <Group p="md" position="apart" align="center">
              <TextInput
                placeholder="Search users..."
                value={search}
                onChange={(e) => setSearch(e.currentTarget.value)}
                sx={(theme) => ({
                  width: 300,
                  [theme.fn.smallerThan('sm')]: { width: '100%' },
                })}
              />
            </Group>

            <ScrollArea>
            <Table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Last Login</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers?.map((user: User) => (
                  <tr
                    key={user.id}
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      setSelectedUser(user);
                      setUserDetailsModalOpen(true);
                    }}
                  >
                    <td>
                      <Text>
                        {user.firstName} {user.lastName}
                      </Text>
                    </td>
                    <td>{user.email}</td>
                    <td>
                      <Badge color={user.role === "SUPERUSER" ? "red" : "blue"}>
                        {user.role}
                      </Badge>
                    </td>
                    <td>
                      <Badge
                        color={
                          user.status === "ACTIVE"
                            ? "green"
                            : user.status === "SUSPENDED"
                            ? "red"
                            : "gray"
                        }
                      >
                        {user.status}
                      </Badge>
                    </td>
                    <td>
                      <Text size="sm" color="dimmed">
                        {user.lastLoginAt
                          ? new Date(user.lastLoginAt).toLocaleString()
                          : "Never"}
                      </Text>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            </ScrollArea>
          </Card>
        </Tabs.Panel>

        <Tabs.Panel value="roles">
          <SimpleGrid
            cols={2}
            spacing="md"
            breakpoints={[{ maxWidth: "sm", cols: 1 }]}
          >
            {Object.values(UserRole).map((role) => renderPermissionCard(role))}
          </SimpleGrid>
        </Tabs.Panel>
      </Tabs>

      <ConfirmationModal
        opened={suspendModalOpen}
        onClose={() => setSuspendModalOpen(false)}
        onConfirm={() => {
          suspendUser.mutate(selectedUser.id);
          setSuspendModalOpen(false);
        }}
        title="Suspend User"
        message={`Are you sure you want to suspend ${selectedUser?.firstName} ${selectedUser?.lastName}? They will no longer be able to access the platform.`}
        confirmLabel="Suspend User"
        isLoading={suspendUser.isLoading}
      />

      <ConfirmationModal
        opened={activateModalOpen}
        onClose={() => setActivateModalOpen(false)}
        onConfirm={() => {
          activateUser.mutate(selectedUser.id);
          setActivateModalOpen(false);
        }}
        title="Activate User"
        message={`Are you sure you want to activate ${selectedUser?.firstName} ${selectedUser?.lastName}? They will regain access to the platform.`}
        confirmLabel="Activate User"
        isLoading={activateUser.isLoading}
      />

      {/* User Details Modal */}
      <Modal
        opened={userDetailsModalOpen}
        onClose={() => {
          setUserDetailsModalOpen(false);
          setSelectedUser(null);
        }}
        title={
          selectedUser && (
            <Group spacing="sm">
              <div>
                <Group spacing="sm" align="center">
                  <Text weight={600} size="lg">
                    {selectedUser.firstName} {selectedUser.lastName}
                  </Text>
                  <Badge
                    color={selectedUser.role === "SUPERUSER" ? "red" : "blue"}
                    variant="light"
                    size="sm"
                  >
                    {selectedUser.role}
                  </Badge>
                </Group>
                <Text size="sm" color="dimmed">
                  {selectedUser.email}
                </Text>
              </div>
            </Group>
          )
        }
        size="xl"
        styles={{
          // header: { borderBottom: '1px solid #e9ecef' },
          body: { padding: 0 },
        }}
      >
        {selectedUser && (
          <Tabs defaultValue="basic" variant="outline">
            <Tabs.List pt="md" pr="md" pl="md" sx={{ flexWrap: 'nowrap', overflowX: 'auto' }}>
              <Tabs.Tab value="basic" icon={<IconUser size={14} />}>
                Basic Info
              </Tabs.Tab>
              <Tabs.Tab value="permissions" icon={<IconSettings size={14} />}>
                Permissions
              </Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="basic" p="xl">
              <Paper withBorder>
                <Box p="md" style={{ borderBottom: "1px solid #e9ecef" }}>
                  <Group position="apart">
                    <Text weight={600} size="sm">
                      Basic Information
                    </Text>
                    {isAdmin && selectedUser.role !== "SUPERUSER" && (
                      <Group spacing="xs">
                        {selectedUser.status === "ACTIVE" ? (
                          <Button
                            variant="outline"
                            color="red"
                            size="xs"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSuspendModalOpen(true);
                              setUserDetailsModalOpen(false);
                            }}
                          >
                            Suspend
                          </Button>
                        ) : (
                          <Button
                            variant="outline"
                            color="green"
                            size="xs"
                            onClick={(e) => {
                              e.stopPropagation();
                              setActivateModalOpen(true);
                              setUserDetailsModalOpen(false);
                            }}
                          >
                            Activate
                          </Button>
                        )}
                      </Group>
                    )}
                  </Group>
                </Box>
                <Box p="md">
                  <Grid>
                    <Grid.Col span={12} sm={6}>
                      <Stack spacing="lg">
                        <div>
                          <Text
                            size="xs"
                            color="dimmed"
                            weight={600}
                            tt="uppercase"
                            lts={1}
                            mb={4}
                          >
                            Personal Details
                          </Text>
                          <Stack spacing="xs">
                            <div>
                              <Text size="xs" color="dimmed" mb={4}>
                                Full Name
                              </Text>
                              <Text size="sm" weight={500}>
                                {selectedUser.firstName} {selectedUser.lastName}
                              </Text>
                            </div>
                            <div>
                              <Text size="xs" color="dimmed" mb={4}>
                                Email Address
                              </Text>
                              <Text size="sm">{selectedUser.email}</Text>
                            </div>
                            <div>
                              <Text size="xs" color="dimmed" mb={4}>
                                GitHub Username
                              </Text>
                              <Text size="sm">
                                {selectedUser.githubUsername || "Not set"}
                              </Text>
                            </div>
                          </Stack>
                        </div>
                      </Stack>
                    </Grid.Col>
                    <Grid.Col span={12} sm={6}>
                      <Stack spacing="lg">
                        <div>
                          <Text
                            size="xs"
                            color="dimmed"
                            weight={600}
                            tt="uppercase"
                            lts={1}
                            mb={4}
                          >
                            Account Status
                          </Text>
                          <Stack spacing="xs">
                            <div>
                              <Text size="xs" color="dimmed" mb={4}>
                                Role
                              </Text>
                              <Group spacing="xs">
                                <Badge
                                  color={
                                    selectedUser.role === "SUPERUSER"
                                      ? "red"
                                      : "blue"
                                  }
                                  variant="light"
                                  size="sm"
                                >
                                  {selectedUser.role}
                                </Badge>
                                {selectedUser.role !== "SUPERUSER" && (
                                  <Text size="xs" color="dimmed">
                                    Managed in Keycloak
                                  </Text>
                                )}
                              </Group>
                            </div>
                            <div>
                              <Text size="xs" color="dimmed" mb={4}>
                                Status
                              </Text>
                              <Badge
                                color={
                                  selectedUser.status === "ACTIVE"
                                    ? "green"
                                    : selectedUser.status === "SUSPENDED"
                                    ? "red"
                                    : "gray"
                                }
                                variant="light"
                                size="sm"
                              >
                                {selectedUser.status}
                              </Badge>
                            </div>
                            <div>
                              <Text size="xs" color="dimmed" mb={4}>
                                Created At
                              </Text>
                              <Text size="sm">
                                {new Date(
                                  selectedUser.createdAt
                                ).toLocaleString()}
                              </Text>
                            </div>
                            <div>
                              <Text size="xs" color="dimmed" mb={4}>
                                Last Login
                              </Text>
                              <Text size="sm">
                                {selectedUser.lastLoginAt
                                  ? new Date(
                                      selectedUser.lastLoginAt
                                    ).toLocaleString()
                                  : "Never"}
                              </Text>
                            </div>
                          </Stack>
                        </div>
                      </Stack>
                    </Grid.Col>
                  </Grid>
                </Box>
              </Paper>
            </Tabs.Panel>

            <Tabs.Panel value="permissions" p="xl">
              <Paper withBorder>
                <Box p="md" style={{ borderBottom: "1px solid #e9ecef" }}>
                  <Text weight={600} size="sm">
                    Role & Permissions
                  </Text>
                </Box>
                <Box p="md">
                  <Grid>
                    <Grid.Col span={12}>
                      <div>
                        <Text
                          size="xs"
                          color="dimmed"
                          weight={600}
                          tt="uppercase"
                          lts={1}
                          mb={4}
                        >
                          Role Description
                        </Text>
                        <Text size="sm" mb="lg">
                          {roleDescriptions[selectedUser.role as UserRole]}
                        </Text>
                      </div>
                    </Grid.Col>
                    {rolePermissions[selectedUser.role as UserRole] && (
                      <Grid.Col span={12}>
                        <div>
                          <Text
                            size="xs"
                            color="dimmed"
                            weight={600}
                            tt="uppercase"
                            lts={1}
                            mb={4}
                          >
                            Permissions Matrix
                          </Text>
                          <Grid>
                            <Grid.Col span={12} sm={6}>
                              <div>
                                <Text size="xs" color="dimmed" mb={4}>
                                  Services
                                </Text>
                                <Text size="sm">
                                  {Object.entries(
                                    rolePermissions[
                                      selectedUser.role as UserRole
                                    ].services
                                  )
                                    .filter(([_, value]) => value)
                                    .map(([key]) => key)
                                    .join(", ") || "None"}
                                </Text>
                              </div>
                            </Grid.Col>
                            <Grid.Col span={12} sm={6}>
                              <div>
                                <Text size="xs" color="dimmed" mb={4}>
                                  Environments
                                </Text>
                                <Text size="sm">
                                  {Object.entries(
                                    rolePermissions[
                                      selectedUser.role as UserRole
                                    ].environments
                                  )
                                    .filter(([_, value]) => value)
                                    .map(([key]) => key)
                                    .join(", ") || "None"}
                                </Text>
                              </div>
                            </Grid.Col>
                            <Grid.Col span={12} sm={6}>
                              <div>
                                <Text size="xs" color="dimmed" mb={4}>
                                  Variables
                                </Text>
                                <Text size="sm">
                                  {Object.entries(
                                    rolePermissions[
                                      selectedUser.role as UserRole
                                    ].variables
                                  )
                                    .filter(([_, value]) => value)
                                    .map(([key]) => key)
                                    .join(", ") || "None"}
                                </Text>
                              </div>
                            </Grid.Col>
                            <Grid.Col span={12} sm={6}>
                              <div>
                                <Text size="xs" color="dimmed" mb={4}>
                                  Users
                                </Text>
                                <Text size="sm">
                                  {Object.entries(
                                    rolePermissions[
                                      selectedUser.role as UserRole
                                    ].users
                                  )
                                    .filter(([_, value]) => value)
                                    .map(([key]) => key)
                                    .join(", ") || "None"}
                                </Text>
                              </div>
                            </Grid.Col>
                          </Grid>
                        </div>
                      </Grid.Col>
                    )}
                  </Grid>
                </Box>
              </Paper>
            </Tabs.Panel>
          </Tabs>
        )}
      </Modal>
    </PageTransition>
  );
}
